import type { Optional } from '@vimeo/core/types/generic';

import { runAsyncWithRetry } from '../../utils/async';
import { getTimeUntilExpiration, parseJWT } from '../../utils/jwt';
import { EventEmitter } from '../EventEmitter';
import { IntegrationLogger } from '../IntegrationLogger';

type TTokenRefreshHandler = (newAccessToken: string) => void;

// TODO validate access and refresh tokens (probably move initial validation to @vimeo/vpaas-sdk)
// TODO refresh accessToken
// TODO get user info and expose it
// TODO (?) visibilitychange - don't rely on browser callbacks dedup behaviour and wrap into condition
export class TokenService {
  private static readonly TOKEN_REFRESH_RETRIES = 5;
  private static readonly TOKEN_REFRESH_RETRY_INTERVAL_MS = 1_000;
  // token will be refreshed TOKEN_REFRESH_OFFSET_MS before the token expiration time
  private static readonly TOKEN_REFRESH_OFFSET_MS = 5 * 60 * 1_000;
  // It's not for the security reasons, but rather for protection from cases,
  // where, for some reason, we will start to receive expired access tokens (cache issue etc.),
  // and will get into an infinite loop of refreshes.
  // So if we'll refresh more than N times, stop the rotation
  // MAX_TOKEN_REFRESH_ITERATIONS*(tokenTTL-TOKEN_REFRESH_OFFSET_MS) = should be enough for all cases
  private static readonly MAX_TOKEN_REFRESH_ITERATIONS = 250;

  private readonly log: IntegrationLogger = IntegrationLogger.create('TokenService');
  private readonly eventEmitter: EventEmitter<{
    tokenRefresh: [string];
  }> = new EventEmitter();

  private accessToken: Optional<string> = null;
  private refreshToken: Optional<string> = null;

  private tokenRefreshTimer: Optional<number> = null;
  private tokenRefreshPromise: Optional<Promise<Optional<string>>> = null;

  private isTokenRefreshEnabled: boolean = false;
  private successfulTokenRefreshIterationsCount: number = 0;

  constructor() {
    this.handleWindowVisibilityChange = this.handleWindowVisibilityChange.bind(this);
  }

  public setTokens(accessToken: string, refreshToken: string): void {
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    if (this.isTokenRefreshEnabled) {
      this.stopTokenAutoRefresh();
      this.startTokenAutoRefresh();
    }
  }

  public onTokenRefresh(onTokenRefreshCallback: TTokenRefreshHandler): void {
    this.eventEmitter.on('tokenRefresh', onTokenRefreshCallback);
  }

  public offTokenRefresh(onTokenRefreshCallback: TTokenRefreshHandler): void {
    this.eventEmitter.off('tokenRefresh', onTokenRefreshCallback);
  }

  public startTokenAutoRefresh(): void {
    if (this.successfulTokenRefreshIterationsCount >= TokenService.MAX_TOKEN_REFRESH_ITERATIONS) {
      this.log.warn('not starting token auto refresh due to max refreshes amount reached');
      return;
    }

    this.clearTokenRefreshTimer();

    if (!this.accessToken || !this.refreshToken) {
      this.log.warn(
        'not starting token refresh because refresh or access tokens or both are not set',
      );
      return;
    }
    // TODO we don't really need to parse refresh token,
    //  we can remove this and completely rely on the 'token validation' step (when it will be implemented)
    if (!parseJWT(this.refreshToken)) {
      this.log.warn('invalid refresh token, not starting token rotation');
      return;
    }
    const accessTokenTimeUntilExpiration = getTimeUntilExpiration(this.accessToken);
    if (accessTokenTimeUntilExpiration === null) {
      this.log.warn('invalid access token, not starting token rotation');
      return;
    }

    const refreshTime = Math.max(
      accessTokenTimeUntilExpiration - TokenService.TOKEN_REFRESH_OFFSET_MS,
      0,
    );
    this.tokenRefreshTimer = window.setTimeout(() => void this.refreshAccessToken(), refreshTime);
    this.isTokenRefreshEnabled = true;

    document.addEventListener('visibilitychange', this.handleWindowVisibilityChange);
    this.log.info(`token refresh scheduled in ${Math.round(refreshTime / 1000)} seconds`);
  }

  public stopTokenAutoRefresh(): void {
    this.isTokenRefreshEnabled = false;
    this.clearTokenRefreshTimer();
    document.removeEventListener('visibilitychange', this.handleWindowVisibilityChange);
    this.log.info('token auto refresh stopped');
  }

  public async forceTokenRefresh(): Promise<Optional<string>> {
    if (!this.accessToken || !this.refreshToken) {
      this.log.warn(
        'not starting forceTokenRefresh because refresh or access tokens or both are not set',
      );
      return null;
    }
    return this.refreshAccessToken();
  }

  private refreshAccessToken(): Promise<Optional<string>> {
    if (this.tokenRefreshPromise) {
      return this.tokenRefreshPromise;
    }
    this.clearTokenRefreshTimer();
    this.tokenRefreshPromise = (async () => {
      try {
        this.log.info('refreshing token');
        this.accessToken = await runAsyncWithRetry(
          TokenService.TOKEN_REFRESH_RETRIES,
          TokenService.TOKEN_REFRESH_RETRY_INTERVAL_MS,
          () => this.getNewAccessToken(),
        );
        if (this.isTokenRefreshEnabled) {
          this.startTokenAutoRefresh();
        }
        this.successfulTokenRefreshIterationsCount += 1;
        this.log.info('token refreshed successfully');
        this.eventEmitter.emit('tokenRefresh', this.accessToken);
        return this.accessToken;
      } catch (error) {
        this.log.error(
          `failed to refresh token after ${TokenService.TOKEN_REFRESH_RETRIES} retries`,
          error,
        );
        return null;
      } finally {
        this.tokenRefreshPromise = null;
      }
    })();
    return this.tokenRefreshPromise;
  }

  private handleWindowVisibilityChange(): void {
    if (
      document.visibilityState !== 'visible' ||
      this.tokenRefreshPromise ||
      !this.isTokenRefreshEnabled
    ) {
      return;
    }
    if (!this.accessToken || !this.refreshToken) {
      this.log.warn(
        'visibilitychange: token refresh is enabled, but accessToken or refreshToken or both are not set',
      );
      return;
    }
    this.startTokenAutoRefresh();
  }

  private getNewAccessToken(): Promise<string> {
    return Promise.reject('getNewAccessToken mocked, cannot retrieve new token');
  }

  private clearTokenRefreshTimer(): void {
    if (this.tokenRefreshTimer) {
      window.clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }
}
